import { Component } from '@angular/core';
import { RootScopeService } from 'src/app/services/root-scope.service';
import moment from 'moment';
import { MatDialog } from '@angular/material/dialog';
import { CustomerAlertsService } from 'src/app/services/customer-alerts.service';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import * as Constants from '../../../models/Constants';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';

@Component({
  selector: 'app-view-expired-alerts',
  templateUrl: './view-expired-alerts.component.html',
  styleUrls: ['./view-expired-alerts.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: Constants.MY_FORMATS },
  ],
})

export class ViewExpiredAlertsComponent {

  noRecordFoundMsg: string = Constants.VIEW_EXPIRED_ALERT.INITIAL_TABLE_MSG;
  apiFailureMsg: string = Constants.VIEW_EXPIRED_ALERT.API_FAILURE;
  noExpiredAlertSelected: string = Constants.VIEW_EXPIRED_ALERT.NO_ALERT_SELECTED;
  
  maxDate: Date = new Date();
  minDate: Date = moment().subtract(2, 'years').add(1, 'day').toDate();
  displayedColumns: string[] = ['title', 'author', 'expiration'];

  constructor(
    public rootScope: RootScopeService,
    public dialog: MatDialog,
    public customerAlertsService: CustomerAlertsService
  ) { }

  
  /**
   * Returns the minimum date for expired alerts.
   * If the start date is available in the `expiredAlertsDateRangeForm` form, it returns the start date.
   * Otherwise, it returns the `minDate`.
   *
   * @returns The minimum date for expired alerts.
   */
  minDateCheck() {
    if (this.customerAlertsService.expiredAlertsDateRangeForm?.value?.start) {
      return this.customerAlertsService.expiredAlertsDateRangeForm?.value?.start;
    }
    return this.minDate;
  }


  /**
   * Closes the alert popup for viewing expired alerts.
   */
  closeAlertpopup() {
    this.rootScope.activeCustTab.viewExpiredAlert = false;
  }

  /**
   * Handles the event when the date is changed in the datepicker.
   *
   * @param event - The MatDatepickerInputEvent object containing the selected date.
   */
  onDateChange(event: MatDatepickerInputEvent<Date>) {
    this.rootScope.activeCustTab.viewExpiredAlertData.startDate = this.customerAlertsService.expiredAlertsDateRangeForm?.value?.start;
    this.rootScope.activeCustTab.viewExpiredAlertData.endDate = this.customerAlertsService.expiredAlertsDateRangeForm?.value?.end;
  }


  /**
   * Searches for expired alerts based on the selected date range.
   * 
   * If the form for the expired alerts date range is invalid, the method will return early.
   * 
   * @remarks
   * The method retrieves the start and end dates from the expired alerts date range form and formats them as 'YYYY-MM-dd'.
   * It then calls the `getExpiredAlerts` method of the `customerAlertsService` with the formatted start and end dates.
   */
  searchExpiredAlerts() {
    if (this.customerAlertsService.expiredAlertsDateRangeForm?.invalid) {
      return;
    }

    const { start, end } = this.customerAlertsService.expiredAlertsDateRangeForm?.value;
    const startDate = this.rootScope.dateFormatter(start, 'YYYY-MM-dd');
    const endDate = this.rootScope.dateFormatter(end, 'YYYY-MM-dd');
    this.customerAlertsService.getExpiredAlerts(startDate, endDate);
  }


  /**
   * Handles the single click event on a row.
   * 
   * @param row - The selected row object.
   */
  onRowSingleClick(row: any) {
    this.rootScope.activeCustTab.viewExpiredAlertData.selectedRow = row;
    this.selectedRowData(row);
  }


  /**
   * Updates the selected row data in the viewExpiredAlertData property of the activeCustTab.
   * @param row - The row object representing the selected row.
   */
  selectedRowData(row: any) {
    this.rootScope.activeCustTab.viewExpiredAlertData.selectedRowDetailData = this.rootScope.activeCustTab.viewExpiredAlertData?.viewExpiredAlerts.find(item => item?.runtimeAlertId === row?.runtimeAlertId);
  }


  /**
   * Checks if there are no records in the viewExpiredAlerts array.
   * 
   * @returns {boolean} Returns true if there are no records, otherwise false.
   */
  noRecord() {
    if (this.rootScope.activeCustTab.viewExpiredAlertData?.viewExpiredAlerts?.length == 0) {

      if (this.rootScope.activeCustTab.viewExpiredAlertData?.searchExpiredAlerts) {
        this.noRecordFoundMsg = Constants.VIEW_EXPIRED_ALERT.NO_RECORD_FOUND;
      }
      return true;
    }
    return false;
  }



  /**
   * Checks if a selected row has valid data in the viewExpiredAlertData property.
   * 
   * @returns A boolean value indicating whether a selected row has valid data.
   */
  checkSelectedRow(): boolean {
    if (this.rootScope.activeCustTab.viewExpiredAlertData?.selectedRowDetailData && Object?.keys(this.rootScope.activeCustTab?.viewExpiredAlertData?.selectedRowDetailData)?.length > 0) {
      return true;
    }
    return false;
  }


  /**
   * Checks if the date range in the expired alerts form is incorrect.
   * 
   * @returns {boolean} Returns true if the date range is incorrect, otherwise false.
   */
  incorrectDateRange(): boolean {
    if (this.customerAlertsService?.expiredAlertsDateRangeForm &&
      this.customerAlertsService?.expiredAlertsDateRangeForm?.controls?.start?.value &&
      this.customerAlertsService?.expiredAlertsDateRangeForm?.controls?.end?.value &&
      this.customerAlertsService?.expiredAlertsDateRangeForm?.invalid &&
      this.customerAlertsService?.expiredAlertsDateRangeForm?.touched) {
      return true;
    }
    return false;
  }


}